// 채우기 핸들(선택 영역 오른쪽 아래 모서리를 끌어내리기)의 엑셀식 연속 값.
// 그리드는 원본을 반복 복사만 하므로, 끌어내린 뒤 이 규칙으로 값을 다시 계산한다.
//
// 그냥 끌면 늘 똑같이 복사, Ctrl 을 누른 채 끌면 자동 증가 (커서에 + 표시) — 사용자 결정. Ctrl 일 때:
//  · 한 칸: 숫자면 +1 (1 → 2, 3 …), 글자 속 숫자면 마지막 숫자 +1 (1-3x0 → 1-3x1, 110_CY1_PV → 110_CY2_PV)
//  · 두 칸 이상이 같은 꼴이고 숫자 한 자리만 바뀜 → 그 자리를 마지막 두 값의 간격으로 이어감
//    (1-3x0, 2-3x0 → 3-3x0, 4-3x0 …   /   10, 20 → 30, 40 …)
//  · 숫자가 없거나 꼴이 다르면 → 복사 (반복)
// null 을 돌려주면 "복사 그대로"(그리드가 이미 넣은 값 유지).

const NUMBER = /^-?\d+(\.\d+)?$/
// 한 칸일 때 쓰는 마지막 숫자 덩어리와 앞뒤 — "1-3x0" → ["1-3x", "0", ""], "110_CY1_PV" → ["110_CY", "1", "_PV"]
const LAST_NUMBER = /^(.*?)(\d+)(\D*)$/

// 소수 계산 찌꺼기 제거 (0.1 + 0.2)
const formatNumber = (n: number) => String(Number(n.toFixed(10)))

// 앞자리 0 유지 (D0009 → D0010)
function pad(n: number, like: string) {
  if (n < 0) return String(n)
  const s = String(n)
  return like.length > 1 && like.startsWith('0') ? s.padStart(like.length, '0') : s
}

/** 원본 칸들(src, 위→아래)을 k 번째(1부터) 아래 칸으로 이어 갈 값. null = 복사 그대로 */
export function seriesValue(src: string[], k: number, ctrl: boolean): string | null {
  const values = src.map(v => v ?? '')
  const n = values.length
  if (n === 0 || !ctrl) return null

  if (n === 1) {
    const v = values[0].trim()
    if (NUMBER.test(v)) return formatNumber(Number(v) + k)
    const m = LAST_NUMBER.exec(values[0])
    return m ? m[1] + pad(Number(m[2]) + k, m[2]) + m[3] : null
  }

  if (values.every(v => NUMBER.test(v.trim()))) {
    const a = Number(values[n - 2]), b = Number(values[n - 1])
    return formatNumber(b + (b - a) * k)
  }

  // 숫자 자리를 나눠 비교 — 글자 부분이 모두 같고 바뀌는 숫자 자리가 하나면 그 자리를 이어 간다
  // ("1-3x0","2-3x0" → 국번 자리, "1-3x0","1-3x2" → 주소 자리)
  const tokens = values.map(v => v.split(/(\d+)/))
  const shape = tokens[0]
  if (tokens.some(t => t.length !== shape.length || t.some((part, i) => i % 2 === 0 && part !== shape[i]))) return null
  const changing = shape.map((_, i) => i).filter(i => i % 2 === 1 && tokens.some(t => t[i] !== shape[i]))
  if (changing.length !== 1) return null
  const at = changing[0]
  const a = Number(tokens[n - 2][at]), b = Number(tokens[n - 1][at])
  const next = b + (b - a) * k
  if (next < 0) return null
  const out = [...tokens[n - 1]]
  out[at] = pad(next, tokens[n - 1][at])
  return out.join('')
}
